import { UseFormReturn, FieldValues, Controller, RegisterOptions } from "react-hook-form";
import { useRecords } from "../../api/useRecords";
import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

export type AsyncAutoCompleteNewProps = {
    entityForm: UseFormReturn<FieldValues>;
    fieldName: string;
    disabled?: boolean;
    placeholder?: string;
    rules?: RegisterOptions<FieldValues, string>;
    recordsOptions: {
        entity: string;
        fieldsToQuery: string;
        filter?: any;
        customFilter?: (value: string) => any;
        labelKey: string | ((option: any) => string);
        idFromParams?: string;
        sort?: string;
        limit?: number;
        renderOption?: (props: any, option: any) => React.ReactNode;
    };
    enabled?: boolean;
};

export const AsyncAutoCompleteNew = ({
    entityForm,
    fieldName,
    disabled,
    placeholder,
    recordsOptions,
    rules,
    enabled,
}: AsyncAutoCompleteNewProps) => {
    const { entity, fieldsToQuery, filter, customFilter, idFromParams } = recordsOptions;
    const [queryDetails, setQueryDetails] = useState<any>(null);
    const [inputValue, setInputValue] = useState<string>("");
    const [items, setItems] = useState<readonly any[]>([]);


    const { data, isInitialLoading, refetch } = useRecords(entity, fieldsToQuery, {
        extraOptions: {
            filter: queryDetails?.filter,
            sort: recordsOptions.sort,
            pageSize: recordsOptions.limit,
        },
        enabled: queryDetails || enabled ? true : false,
    });

    
    useEffect(() => {
        let newQueryDetails = queryDetails;
        if (filter && (!queryDetails || queryDetails.filter !== filter)) {
            newQueryDetails = { filter: filter };
        }
        if (idFromParams && !queryDetails) {
            newQueryDetails = {
                entity: recordsOptions.entity,
                fieldsToQuery: fieldsToQuery,
                filter: { id: { _eq: idFromParams } },
            };
        }
        if (newQueryDetails !== queryDetails) {
            setQueryDetails(newQueryDetails);
        }
    }, [filter, idFromParams, queryDetails, refetch, fieldsToQuery, recordsOptions.entity]);

    useEffect(() => {
        if (!data) return;

        const currentValue = entityForm.getValues(fieldName);
        const newItems = [...data.items];

        if (currentValue && !newItems.some((item) => item.id === currentValue.id)) {
            newItems.unshift(currentValue);
        }

        setItems(newItems);

        if (idFromParams && data.items.length > 0) {
            const selectedItem = data.items[0];
            entityForm.setValue(fieldName, selectedItem);
            setInputValue(
                typeof recordsOptions.labelKey === "function"
                    ? recordsOptions.labelKey(selectedItem)
                    : selectedItem[recordsOptions.labelKey] || ""
            );
        }
    }, [data]);

    const debouncedSave = useCallback(
        debounce((newValue: string) => {
            if (newValue.length > 2) {
                setQueryDetails({
                    entity: recordsOptions.entity,
                    fieldsToQuery: fieldsToQuery,
                    filter: customFilter ? customFilter(newValue) : filter,
                });
            }
        }, 500),
        [customFilter, filter, fieldsToQuery, recordsOptions.entity]
    );

    const updateValue = (newValue: string) => {
        setInputValue(newValue);
        debouncedSave(newValue);
    };

  
    
    return (
        <Controller
            name={fieldName}
            control={entityForm.control}
            defaultValue={null}
            rules={rules}
            render={({ field }) => (
                <Autocomplete
                    {...field}
                    onInputChange={(_, newInputValue) => {
                        setInputValue(newInputValue);
                        if (newInputValue.length > 2) {
                            updateValue(newInputValue);
                        }
                    }}
                    fullWidth
                    onChange={(_, newValue) => {
                        field.onChange(newValue);
                    }}
                    options={items}
                    loading={isInitialLoading}
                    disabled={disabled}
                    isOptionEqualToValue={(option, value) => {
                        return option.id === value.id;
                    }}
                    getOptionLabel={(option) => {
                        if (!option) return "";

                        if (typeof recordsOptions.labelKey === "function") {
                            return recordsOptions.labelKey(option);
                        }

                        return option[recordsOptions.labelKey] || "";
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            value={inputValue}
                            label={placeholder || "Pesquisar..."}
                            size="medium"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: <>{isInitialLoading ? <CircularProgress color="inherit" size={20} /> : null}</>,
                            }}
                            helperText={entityForm.getValues(fieldName)?.quick_note || ''}
                            FormHelperTextProps={{
                                style: { color: 'red' }
                            }}
                        />
                    )}
                    renderOption={
                        recordsOptions.renderOption ? (props, option) => recordsOptions.renderOption!(props, option) : undefined
                    }
                />
            )}
        />
    );
};
